import { useState, useCallback, useRef } from 'react';
import type { ContentSection } from '../types/content';

interface ContentCache {
  [path: string]: string;
}

interface UseStoryContentReturn {
  /**
   * Load content for a section from contentPath if needed
   */
  loadSectionContent: (section: ContentSection) => Promise<string>;
  /**
   * Preload content for upcoming sections
   */
  preloadSections: (sections: ContentSection[]) => void;
  /**
   * Get cached content for a section
   */
  getCachedContent: (contentPath: string) => string | undefined;
  /**
   * Clear the content cache
   */
  clearCache: () => void;
  /**
   * Check if content is currently loading
   */
  isLoading: boolean;
  /**
   * Current loading error if any
   */
  error: Error | null;
}

/**
 * Hook for lazy loading story content from separate .txt files
 * 
 * Usage:
 * const { loadSectionContent, preloadSections } = useStoryContent({ categoryId: 'scifi' });
 * 
 * // Load current section
 * const content = await loadSectionContent(currentSection);
 * 
 * // Preload next sections
 * preloadSections([nextSection, sectionAfterThat]);
 */
export function useStoryContent(categoryId?: string): UseStoryContentReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Use ref for cache to avoid re-renders
  const contentCacheRef = useRef<ContentCache>({});
  const loadingPromisesRef = useRef<Map<string, Promise<string>>>(new Map());

  /**
   * Load content from a path
   */
  const loadFromPath = useCallback(async (contentPath: string): Promise<string> => {
    // Check cache first
    if (contentCacheRef.current[contentPath]) {
      return contentCacheRef.current[contentPath];
    }

    // Check if already loading
    const existingPromise = loadingPromisesRef.current.get(contentPath);
    if (existingPromise) {
      return existingPromise;
    }

    // Build full URL - contentPath is relative to category directory
    // e.g., "stories/the-last-transmission/section-0.txt"
    // categoryId is the category prefix (e.g., 'scifi', 'historical')
    const baseUrl = window.location.origin;
    const categoryPrefix = categoryId ? `${categoryId}/` : '';
    const fullUrl = `${baseUrl}/data/categories/${categoryPrefix}${contentPath}`;

    // Create loading promise
    const loadPromise = fetch(fullUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load content: ${response.status} ${response.statusText}`);
        }
        return response.text();
      })
      .then(text => {
        // Cache the result
        contentCacheRef.current[contentPath] = text;
        loadingPromisesRef.current.delete(contentPath);
        return text;
      })
      .catch(err => {
        loadingPromisesRef.current.delete(contentPath);
        throw err;
      });

    loadingPromisesRef.current.set(contentPath, loadPromise);
    return loadPromise;
  }, [categoryId]);

  /**
   * Load content for a section
   */
  const loadSectionContent = useCallback(async (section: ContentSection): Promise<string> => {
    // If content is already provided (legacy/inline), use it
    if (section.content) {
      return section.content;
    }

    // If contentPath is provided, load from file
    if (section.contentPath) {
      setIsLoading(true);
      setError(null);
      
      try {
        const content = await loadFromPath(section.contentPath);
        setIsLoading(false);
        return content;
      } catch (err) {
        setIsLoading(false);
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        throw error;
      }
    }

    throw new Error('Section has neither content nor contentPath');
  }, [loadFromPath]);

  /**
   * Preload multiple sections in the background
   */
  const preloadSections = useCallback((sections: ContentSection[]) => {
    sections.forEach(section => {
      // Skip if already has content or no contentPath
      if (section.content || !section.contentPath) {
        return;
      }

      // Skip if already cached or loading
      if (contentCacheRef.current[section.contentPath] || 
          loadingPromisesRef.current.has(section.contentPath)) {
        return;
      }

      // Load in background (don't await, don't block)
      loadFromPath(section.contentPath).catch(() => {
        // Silently fail for preloads - we'll retry when actually needed
      });
    });
  }, [loadFromPath]);

  /**
   * Get cached content for a path
   */
  const getCachedContent = useCallback((contentPath: string): string | undefined => {
    return contentCacheRef.current[contentPath];
  }, []);

  /**
   * Clear the content cache
   */
  const clearCache = useCallback(() => {
    contentCacheRef.current = {};
    loadingPromisesRef.current.clear();
  }, []);

  return {
    loadSectionContent,
    preloadSections,
    getCachedContent,
    clearCache,
    isLoading,
    error,
  };
}

export default useStoryContent;
